import '../awareness-workspace.css';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Table } from '../../components/ui/Table';
import { Modal } from '../../components/ui/Modal';
import { reportedMessagesApi } from '../../api/reportedMessages';
import type { ReportedMessage, ReportedMessageStatus } from '../../api/reportedMessages';
import { ReportReview } from './ReportReview';
import { ReportAnalysis, VerdictBadge } from './ReportAnalysis';

const statusStyles: Record<ReportedMessage['status'], string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-gray-100 text-gray-600',
};

const PER_PAGE = 20;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Wraps untrusted HTML in a minimal document with a strict Content-Security-
 * Policy so it can be safely rendered inside a sandboxed iframe (sandbox="").
 * The CSP blocks all network loads except inline data: images, and the empty
 * sandbox attribute (set on the iframe itself) blocks script execution and
 * any navigation originating from the frame.
 */
function buildSafeSrcDoc(html: string): string {
  const csp =
    '<meta http-equiv="Content-Security-Policy" content="default-src \'none\'; img-src data:; style-src \'unsafe-inline\'">';
  return `${csp}${html}`;
}

function toStartOfDayISO(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00.000Z`).toISOString();
}

function toEndOfDayISO(dateStr: string): string {
  return new Date(`${dateStr}T23:59:59.999Z`).toISOString();
}

export function ReportedMessageList() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceId = searchParams.get('sourceId');
  const [approveModal, setApproveModal] = useState<{ open: boolean; message: ReportedMessage | null }>({
    open: false,
    message: null,
  });
  const [templateName, setTemplateName] = useState('');
  const [sanitizedSubject, setSanitizedSubject] = useState('');
  const [sanitizedText, setSanitizedText] = useState('');
  const [redactionsConfirmed, setRedactionsConfirmed] = useState(false);

  const [selectedMessage, setDetailMessage] = useState<ReportedMessage | null>(null);
  const [viewAsHtml, setViewAsHtml] = useState(false);
  const detailHeading = useRef<HTMLHeadingElement>(null);
  const queueHeading = useRef<HTMLHeadingElement>(null);

  const [status, setStatus] = useState<ReportedMessageStatus | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  // Debounce the free-text search so we don't refetch on every keystroke.
  // Reset to page 1 once the debounced value actually changes so the user
  // isn't stranded on a now-empty page of a newly-filtered result set.
  useEffect(() => {
    const handle = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const createdAfter = dateFrom ? toStartOfDayISO(dateFrom) : undefined;
  const createdBefore = dateTo ? toEndOfDayISO(dateTo) : undefined;

  const queryParams = {
    status: status || undefined,
    search: search || undefined,
    created_after: createdAfter,
    created_before: createdBefore,
    page,
    per_page: PER_PAGE,
  };

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ['reportedMessages', { status, search, createdAfter, createdBefore, page }],
    queryFn: async () => {
      const res = await reportedMessagesApi.getAll(queryParams);
      return res.data;
    },
    placeholderData: (previousData) => previousData,
  });

  const sourceReportId = sourceId === null ? null : Number(sourceId);
  const validSourceReportId = sourceReportId !== null && Number.isSafeInteger(sourceReportId) && sourceReportId > 0;
  const sourceReportQuery = useQuery({
    queryKey: ['reportedMessageSource', sourceId],
    queryFn: async () => (await reportedMessagesApi.getById(sourceReportId!)).data,
    enabled: sourceId !== null && validSourceReportId,
    retry: false,
  });

  const reportedMessages = data?.data ?? [];
  const detailMessage = sourceId !== null
    ? sourceReportQuery.data ?? null
    : reportedMessages.find(message => message.id === selectedMessage?.id) ?? selectedMessage;
  const sourceError = sourceId !== null && (!validSourceReportId || sourceReportQuery.isError)
    ? validSourceReportId ? 'The source report could not be loaded.' : 'The source report link is invalid.'
    : '';
  const detailId = detailMessage?.id;
  useEffect(() => { if (detailId != null) detailHeading.current?.focus(); }, [detailId]);
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const [showClusters, setShowClusters] = useState(false);

  // Detection verdicts for the visible rows. Analysis runs on-demand
  // server-side, so rows without a stored analysis still resolve.
  const analysisQueries = useQueries({
    queries: reportedMessages.map((m) => ({
      queryKey: ['reportedAnalysis', m.id],
      queryFn: async () => (await reportedMessagesApi.analysis(m.id)).data,
      retry: false,
      staleTime: 60_000,
    })),
  });
  // Cheap enough to compute inline: verdicts already resolved per row.
  const verdictById = new Map<number, string>();
  reportedMessages.forEach((m, i) => {
    const verdict = analysisQueries[i]?.data?.verdict;
    if (verdict) verdictById.set(m.id, verdict);
  });

  const clustersQuery = useQuery({
    queryKey: ['reportedMessageClusters'],
    queryFn: async () => (await reportedMessagesApi.clusters()).data.data,
    enabled: showClusters,
    retry: false,
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string }) => reportedMessagesApi.approve(id, name, { subject: sanitizedSubject, text: sanitizedText, html: '', redactions_confirmed: redactionsConfirmed }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportedMessages'] });
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      setApproveModal({ open: false, message: null });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: (id: number) => reportedMessagesApi.reject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reportedMessages'] });
    },
  });

  const openApproveModal = (message: ReportedMessage) => {
    approveMutation.reset();
    setTemplateName(`From report: ${message.subject}`);
    setSanitizedSubject(message.subject);
    setSanitizedText(message.body_text);
    setRedactionsConfirmed(false);
    setApproveModal({ open: true, message });
  };

  const openDetailModal = (message: ReportedMessage) => {
    setViewAsHtml(false);
    setDetailMessage(message);
  };

  const closeInvestigation = () => {
    if (sourceId !== null) {
      const next = new URLSearchParams(searchParams);
      next.delete('sourceId');
      setSearchParams(next);
    }
    setDetailMessage(null);
  };


  return (
    <div className="investigation-workspace">
      {sourceError && <p role="alert" className="text-red-700">{sourceError}</p>}
      <div className="awareness-heading investigation-heading">
        <div>

          <h1 ref={queueHeading} tabIndex={-1}>Reported messages</h1>
          <p className="text-gray-500 mt-1">Investigate reports, follow the evidence, and record a review decision.</p>
        </div>
        <Button variant="secondary" onClick={() => setShowClusters((v) => !v)}>
          {showClusters ? 'Show reports' : 'Show clusters'}
        </Button>
      </div>

      {rejectMutation.isError && (
        <p role="alert" className="text-red-700">Could not reject this report. Refresh the queue and try again.</p>
      )}

      {showClusters && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Report clusters</h2>
          {clustersQuery.isLoading ? (
            <div className="animate-pulse space-y-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-12 bg-gray-100 rounded"></div>
              ))}
            </div>
          ) : clustersQuery.isError ? (
            <p role="alert" className="text-red-700">Could not load report clusters.</p>
          ) : (clustersQuery.data ?? []).length === 0 ? (
            <p className="text-sm text-gray-500">No clusters yet. Clusters appear once reported messages have been analyzed.</p>
          ) : (
            <Table
              columns={[
                {
                  key: 'key',
                  header: 'Cluster',
                  render: (c) => <span className="font-mono text-sm">{c.key}</span>,
                },
                {
                  key: 'count',
                  header: 'Reports',
                  render: (c) => <span className="text-sm text-gray-900">{c.count}</span>,
                },
                {
                  key: 'verdicts',
                  header: 'Verdicts',
                  render: (c) => (
                    <span className="text-sm text-gray-700">
                      {Object.entries(c.verdicts).map(([v, n]) => `${v}: ${n}`).join(', ')}
                    </span>
                  ),
                },
                {
                  key: 'first_seen',
                  header: 'First seen',
                  render: (c) => (
                    <span className="text-sm text-gray-500">{new Date(c.first_seen).toLocaleDateString()}</span>
                  ),
                },
                {
                  key: 'senders',
                  header: 'Senders',
                  render: (c) => <span className="text-sm text-gray-700">{(c.senders ?? []).join(', ')}</span>,
                },
              ]}
              data={clustersQuery.data ?? []}
              keyExtractor={(c) => c.key}
              emptyMessage="No clusters yet."
            />
          )}
        </Card>
      )}

      {!showClusters && (
      <section className="awareness-surface investigation-queue" aria-label="Report queue">
        <div className="investigation-queue-heading"><h2>Report queue</h2><span>{isError ? "Count unavailable" : isLoading ? "Loading?" : `${total} matching reports`}</span></div>
        <div className="investigation-filters">
          <div>
            <label htmlFor="report-status-filter" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="report-status-filter"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as ReportedMessageStatus | '');
                setPage(1);
              }}
              className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div>
            <label htmlFor="report-search" className="block text-sm font-medium text-gray-700 mb-1">
              Search
            </label>
            <input
              id="report-search"
              type="text"
              placeholder="Reporter email or subject"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="report-date-from" className="block text-sm font-medium text-gray-700 mb-1">
              From
            </label>
            <input
              id="report-date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
              className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="report-date-to" className="block text-sm font-medium text-gray-700 mb-1">
              To
            </label>
            <input
              id="report-date-to"
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
              className="w-full min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          {isFetching && !isLoading && (
            <span className="text-sm text-gray-400">Updating...</span>
          )}
        </div>

        {isError ? (
          <p role="alert" className="text-red-700">Could not load reported messages. Refresh the page to try again.</p>
        ) : isLoading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded"></div>
            ))}
          </div>
        ) : (
          <>
            {reportedMessages.length === 0 ? <p className="awareness-empty">No reported messages yet.</p> : <ul className="investigation-items">{reportedMessages.map(message => <li key={message.id} className={detailMessage?.id === message.id ? 'selected' : ''}>
              <button id={`report-queue-${message.id}`} className="investigation-select" aria-label={`Investigate ${message.subject || "report"}`} aria-pressed={detailMessage?.id === message.id} onClick={() => openDetailModal(message)}>
                <strong>{message.subject || '(No subject)'}</strong><span>{message.reporter_email}</span><span className="investigation-row-meta"><span className={`report-status report-status-${message.status}`}>{message.status}</span><VerdictBadge verdict={verdictById.get(message.id)} /><time dateTime={message.created_at}>{new Date(message.created_at).toLocaleDateString()}</time></span>
              </button>
              <div className="investigation-row-actions">{message.status === 'pending' ? <><Button variant="secondary" size="sm" onClick={() => openApproveModal(message)}>Approve</Button><Button variant="danger" size="sm" loading={rejectMutation.isPending && rejectMutation.variables === message.id} onClick={() => rejectMutation.mutate(message.id)}>Reject</Button></> : <span>{message.reviewed_by ? `Reviewed by ${message.reviewed_by}` : 'Reviewed'}</span>}</div>
            </li>)}</ul>}
            {reportedMessages.length > 0 && (
              <div className="flex flex-wrap gap-3 items-center justify-between mt-4">
                <span className="text-sm text-gray-500">
                  Page {page} of {totalPages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
      )}

      <Modal
        isOpen={approveModal.open}
        onClose={() => setApproveModal({ open: false, message: null })}
        title="Approve Reported Message"
        size="lg"
      >
        <p className="text-gray-600 mb-4">
          Convert "{approveModal.message?.subject}" into a new email template.
        </p>
        <label htmlFor="report-template-name" className="block text-sm font-medium text-gray-700 mb-1">Template name</label>
        <input
          id="report-template-name"
          type="text"
          value={templateName}
          onChange={(e) => setTemplateName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-6 focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
        {approveMutation.isError && (
          <p role="alert" className="text-red-700 mb-4">Could not approve this report. Check the template name or refresh the queue before trying again.</p>
        )}
        <label className="label">Sanitized subject<input className="input" value={sanitizedSubject} onChange={e => { setSanitizedSubject(e.target.value); setRedactionsConfirmed(false); }} /></label>
        <label className="label">Sanitized template text<textarea rows={8} className="input" value={sanitizedText} onChange={e => { setSanitizedText(e.target.value); setRedactionsConfirmed(false); }} /></label>
        <p className="text-sm text-gray-600 my-3">Remove personal information, secrets, and live destinations. Only this reviewed text will become the template.</p>
        <label className="flex gap-2 my-4"><input type="checkbox" checked={redactionsConfirmed} onChange={e => setRedactionsConfirmed(e.target.checked)} />I reviewed and redacted the template content.</label>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setApproveModal({ open: false, message: null })}>
            Cancel
          </Button>
          <Button
            loading={approveMutation.isPending}
            disabled={!redactionsConfirmed || !sanitizedText.trim() || !templateName.trim()}
            onClick={() =>
              approveModal.message &&
              approveMutation.mutate({ id: approveModal.message.id, name: templateName })
            }
          >
            Approve
          </Button>
        </div>
      </Modal>

      {!showClusters && <section className="awareness-surface investigation-detail" aria-labelledby="investigation-detail-title">
        <div className="investigation-detail-heading"><h2 id="investigation-detail-title" ref={detailHeading} tabIndex={-1}>Reported Message Detail</h2>{detailMessage && <Button variant="secondary" size="sm" onClick={() => { const queueItem = document.getElementById(`report-queue-${detailMessage.id}`); (queueItem ?? queueHeading.current)?.focus(); closeInvestigation(); }}>Close investigation</Button>}</div>
        {!detailMessage && <div className="investigation-prompt"><h3>Select a report to investigate</h3><p>The message, detection evidence, and review workflow will appear here. Select another report to keep working through the queue.</p></div>}
        {detailMessage && (
          <div className="space-y-4">
            <ReportReview key={detailMessage.id} message={detailMessage} onChanged={() => { queryClient.invalidateQueries({ queryKey: ['reportedMessages'] }); }} onDeleted={() => { setDetailMessage(null); queryClient.invalidateQueries({ queryKey: ['reportedMessages'] }); }} />
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm break-words">
              <dt className="text-gray-500">Reporter</dt>
              <dd className="text-gray-900">{detailMessage.reporter_email}</dd>
              <dt className="text-gray-500">Subject</dt>
              <dd className="text-gray-900">{detailMessage.subject}</dd>
              <dt className="text-gray-500">Status</dt>
              <dd>
                <span
                  className={`inline-block px-2 py-1 rounded-full text-xs font-medium capitalize ${statusStyles[detailMessage.status]}`}
                >
                  {detailMessage.status}
                </span>
              </dd>
              <dt className="text-gray-500">Reported at</dt>
              <dd className="text-gray-900">{new Date(detailMessage.created_at).toLocaleString()}</dd>
              <dt className="text-gray-500">Reviewed by</dt>
              <dd className="text-gray-900">{detailMessage.reviewed_by || '-'}</dd>
              <dt className="text-gray-500">Reviewed at</dt>
              <dd className="text-gray-900">
                {detailMessage.reviewed_at ? new Date(detailMessage.reviewed_at).toLocaleString() : '-'}
              </dd>
            </dl>

            <ReportAnalysis messageId={detailMessage.id} />

            <div>
              <div className="flex flex-wrap gap-2 items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-700">Message body</h3>
                {detailMessage.body_html && (
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    <input
                      type="checkbox"
                      checked={viewAsHtml}
                      onChange={(e) => setViewAsHtml(e.target.checked)}
                    />
                    View as HTML
                  </label>
                )}
              </div>

              {viewAsHtml && detailMessage.body_html ? (
                <>
                  <p className="text-xs text-gray-500 mb-2">
                    HTML preview is sandboxed: scripts, images, and links are disabled.
                  </p>
                  <iframe
                    title="Reported message HTML preview"
                    srcDoc={buildSafeSrcDoc(detailMessage.body_html)}
                    sandbox=""
                    className="w-full h-80 border border-gray-200 rounded overflow-auto"
                  />
                </>
              ) : (
                <pre className="whitespace-pre-wrap text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded p-3 max-h-80 overflow-auto">
                  {detailMessage.body_text}
                </pre>
              )}
            </div>
          </div>
        )}
      </section>}
    </div>
  );
}
